import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  closeConsentPrompt,
  useConsentPrompt,
} from '@/components/consent/consent-prompt';
import { Button } from '@/components/ui/button';
import { BRAND_NAME } from '@/constants/brand';
import { recordConsent, type ConsentChoice } from '@/lib/consent';

const COOKIE_NOTICE_URL = 'https://coretas.ai/legal/cookies/';
const PRIVACY_STATEMENT_URL = 'https://coretas.ai/privacy.html';

const choose = (choice: ConsentChoice): void => {
  recordConsent(choice);
  closeConsentPrompt();
};

const ConsentBanner: FC = () => {
  const { t } = useTranslation();
  const open = useConsentPrompt();
  if (!open) return null;

  return (
    <section
      aria-label={t('consent.label')}
      className="bg-bg-surface border-border-1 shadow-e2 border-safe-b border-safe-l border-safe-r fixed inset-x-0 bottom-0 z-40 border-t"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <p className="text-fg-3 text-fs-13 max-w-2xl">
          <Trans
            i18nKey="consent.body"
            values={{ brand: BRAND_NAME }}
            components={{
              notice: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href={COOKIE_NOTICE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-fg-link underline"
                />
              ),
              privacy: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href={PRIVACY_STATEMENT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-fg-link underline"
                />
              ),
            }}
          />
        </p>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            className="flex-1 sm:flex-none"
            onClick={() => choose('denied')}
          >
            {t('consent.reject')}
          </Button>
          <Button
            className="flex-1 sm:flex-none"
            onClick={() => choose('granted')}
          >
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export { ConsentBanner };
